import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Calendar, Zap, CheckCircle2, AlertCircle } from "lucide-react";
import { format } from "date-fns";
import { tr as trLocale } from "date-fns/locale";
import { toast } from "sonner";

/**
 * Reusable Quick Payment dialog used across all pending payment tables.
 *
 * Props:
 *  - open: boolean
 *  - onOpenChange: (open: boolean) => void
 *  - title: string                — Modal title
 *  - subject: string              — Who/what is being paid (e.g. "Begüm Demirtaş - Mayıs 2026 maaşı")
 *  - defaultAmount: number        — Pre-filled amount (e.g. pending amount)
 *  - maxAmount?: number           — Optional cap. If provided, amount cannot exceed
 *  - currencySymbol: string
 *  - onSubmit: (data) => Promise<void>   — Called with {amount, date, method, note}
 *  - submitLabel?: string         — Override "Ödemeyi Kaydet"
 *  - testIdPrefix?: string
 *  - infoNote?: string            — Optional informational note shown above form
 */
export default function QuickPaymentDialog({
  open,
  onOpenChange,
  title = "Hızlı Ödeme Al",
  subject,
  defaultAmount = 0,
  maxAmount,
  currencySymbol = "₺",
  onSubmit,
  submitLabel = "Ödemeyi Kaydet",
  testIdPrefix = "quick-pay",
  infoNote,
}) {
  const [amount, setAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [method, setMethod] = useState("cash");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setAmount(defaultAmount ? String(defaultAmount) : "");
      setPaymentDate(format(new Date(), "yyyy-MM-dd"));
      setMethod("cash");
      setNote("");
    }
  }, [open, defaultAmount]);

  const handleSubmit = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      toast.error("Tutar sıfırdan büyük olmalı");
      return;
    }
    if (maxAmount && amt > maxAmount + 0.001) {
      toast.error(`Tutar bekleyen miktardan (${maxAmount} ${currencySymbol}) fazla olamaz`);
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit({
        amount: amt,
        date: paymentDate,
        method,
        note: note.trim(),
      });
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.message || "Ödeme kaydedilemedi");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid={`${testIdPrefix}-modal`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-500" />
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          {subject && (
            <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
              <p className="text-xs text-grey">Ödeme Konusu</p>
              <p className="font-medium text-navy">{subject}</p>
            </div>
          )}

          {infoNote && (
            <div className="p-2 rounded-lg bg-blue-50 border border-blue-200 text-xs text-blue-800 flex gap-2">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <span>{infoNote}</span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Tutar ({currencySymbol}) *</Label>
              <Input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="rounded-xl"
                data-testid={`${testIdPrefix}-amount`}
              />
              {maxAmount ? (
                <p className="text-xs text-grey mt-0.5">
                  Bekleyen: {maxAmount.toLocaleString("tr-TR")} {currencySymbol}
                </p>
              ) : null}
            </div>

            <div>
              <Label>Ödeme Tarihi *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal rounded-xl"
                    data-testid={`${testIdPrefix}-date`}
                  >
                    <Calendar className="mr-2 h-4 w-4" />
                    {paymentDate
                      ? format(new Date(paymentDate), "dd.MM.yyyy", { locale: trLocale })
                      : <span className="text-grey">Seç</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={paymentDate ? new Date(paymentDate) : undefined}
                    onSelect={(d) => d && setPaymentDate(format(d, "yyyy-MM-dd"))}
                    locale={trLocale}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div>
            <Label>Ödeme Yöntemi *</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="rounded-xl" data-testid={`${testIdPrefix}-method`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Nakit</SelectItem>
                <SelectItem value="credit_card">Kredi Kartı</SelectItem>
                <SelectItem value="bank_transfer">Havale / EFT</SelectItem>
                <SelectItem value="other">Diğer</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Not (opsiyonel)</Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Ek not..."
              rows={2}
              className="rounded-xl"
              data-testid={`${testIdPrefix}-note`}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
            className="rounded-xl"
          >
            Vazgeç
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !amount || parseFloat(amount) <= 0}
            className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid={`${testIdPrefix}-submit`}
          >
            <CheckCircle2 className="w-4 h-4 mr-1" />
            {submitting ? "Kaydediliyor..." : submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
